import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Avatar, CircularProgress } from '@mui/material';
import { getUserProfile, updateUserProfile } from '../../services/Apis';
import useResponsive from '../../hooks/useResponsive';
import axios from 'axios';
import apiUrl from '../../Config';
import { useNavigate } from 'react-router-dom';
import { useFilter } from '../../context/FilterContext';

const MyProfileInfo = () => {
  const { isSmallScreen } = useResponsive();
  // const [profile, setProfile] = useState({ name: "", phone: "", email: "", image: "" });
  const { profile, setProfile } = useFilter()
  const [editOpen, setEditOpen] = useState(false);
  const [editProfile, setEditProfile] = useState({ name: "", phone: "", email: "", image: "" });
  const [editImageFile, setEditImageFile] = useState(null);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [orders, setOrders] = useState([]);
  const [errors, setErrors] = useState({ phone: "", email: "", emailExists: "" });
  const fileInputRef = useRef(null);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const { setIsLoggedIn } = useFilter();
  const navigate = useNavigate();

  function getCookie(name) {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : "";
  }
  const token = getCookie('token');
  const removeToken = () => {
    document.cookie = "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  };
  const handleLogout = () => {
    removeToken();
    setIsLoggedIn(false)
    setLogoutOpen(false);
    navigate('/login');
    setProfile({ name: "", phone: "", email: "", image: "" })
  }
  // 

  // Fetch orders on mount
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const res = await axios.get(`${apiUrl}/bookings/getmybooking`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setOrders(res.data.getBookings || []);
      } catch (err) {
        setOrders([]);
      }
    };
    fetchOrders();
  }, [token]);

  // Handle image change
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setIsImageLoading(true);
      setEditImageFile(file);
      const reader = new FileReader();
      reader.onload = (ev) => {
        setEditProfile(prev => ({ ...prev, image: ev.target.result }));
        // Small delay to show loading state
        setTimeout(() => setIsImageLoading(false), 1000);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle save profile
  const handleSaveProfile = async () => {
    if (isSaving) return; // Prevent multiple clicks

    const phoneRegex = /^\d{10}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    let newErrors = { phone: "", email: "", emailExists: "" };
    let hasError = false;

    // Validate phone and email even if unchanged
    if (!phoneRegex.test(editProfile.phone)) {
      newErrors.phone = "Phone number must be exactly 10 digits and contain only numbers.";
      hasError = true;
    }

    if (!emailRegex.test(editProfile.email)) {
      newErrors.email = "Please enter a valid email address.";
      hasError = true;
    }

    setErrors(newErrors);
    if (hasError) return;

    setIsSaving(true);
    try {
      let payload;
      let isFormData = false;

      if (editImageFile) {
        payload = new FormData();
        payload.append("name", editProfile.name);
        payload.append("email", editProfile.email);
        payload.append("phoneNumber", editProfile.phone);
        payload.append("profileimage", editImageFile);
        isFormData = true;
      } else {
        payload = {
          name: editProfile.name,
          email: editProfile.email,
          phoneNumber: editProfile.phone,
          profileimage: profile.image || "",  // Optional: Send existing image or empty string
        };
      }

      await updateUserProfile(payload, isFormData, token);

      // Refresh profile after update
      const updatedUser = await getUserProfile();
      if (updatedUser) {
        setProfile({
          name: updatedUser.name || "",
          phone: updatedUser.phoneNumber || "",
          email: updatedUser.email || "",
          image: updatedUser.imageUrl || ""
        });
        setEditProfile({
          name: updatedUser.name || "",
          phone: updatedUser.phoneNumber || "",
          email: updatedUser.email || "",
          image: updatedUser.imageUrl || ""
        });
      }

      setEditOpen(false);
    } catch (error) {
      console.error("Error saving profile:", error);
      // You might want to show an error message to the user here
    } finally {
      setIsSaving(false);
    }
    setEditImageFile(null);
    setErrors({ phone: "", email: "", emailExists: "" });

  };

  // Add this function
  const handleBuyNow = async (item) => {
    try {
      // If comboId exists and comboOffer is present, treat as combo
      if (item.comboId && item.comboOffer) {
        navigate('/checkout', { state: { ...item.comboOffer, isCombo: true } });
      } else if (item.book) {
        navigate('/checkout', { state: item.book });
      }
    } catch (error) {
      console.log("Failed to add to cart. Please login or try again.");
    }
  };

  return (
    <>
      {/* Profile Info */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          flexDirection: { xs: 'column', md: 'row' },
          width: isSmallScreen ? '100%' : '40%',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          position: { xs: 'relative', md: 'static' }
        }}
      >
        {/* Avatar + Edit */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box
            sx={{
              width: 90,
              height: 90,
              bgcolor: '#e0e0e0',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 40,
              color: '#aaa',
              flexShrink: 0,
              overflow: 'hidden'
            }}
          >
            {profile.image ? (
              <img
                src={profile.image}
                alt="profile"
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
              />
            ) : (
              <Typography sx={{ color: '#aaa', fontSize: 40 }}>
                {profile.name ? profile.name[0] : ""}
              </Typography>
            )}
          </Box>
          <Button
            variant="outlined"
            size="small"
            sx={{
              mt: 1.5,
              borderRadius: 10,
              fontSize: 13,
              textTransform: 'none',
              px: 3,
              borderColor: '#bbb',
              color: '#222',
              fontWeight: 400,
              bgcolor: '#fff'
            }}
            onClick={() => {
              setEditProfile(profile);
              setEditOpen(true);
            }}
          >
            Edit
          </Button>
        </Box>
        {/* Info */}
        <Box sx={{ minWidth: isSmallScreen ? '100%' : 200, placeSelf: 'start', mt: 2 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 20, fontFamily: 'Poppins', textAlign: isSmallScreen ? 'center' : 'left' }}>
            {profile.name}
          </Typography>
          <Typography sx={{ fontSize: 15, color: '#444', mt: 0.5, fontFamily: 'Poppins', textAlign: isSmallScreen ? 'center' : 'left' }}>
            {profile.phone}
          </Typography>
          <Typography sx={{ fontSize: 14, color: '#888', fontFamily: 'Poppins', textAlign: isSmallScreen ? 'center' : 'left' }}>
            {profile.email}
          </Typography>
        </Box>
        <Box
          sx={{
            placeSelf: 'start',
            position: { xs: 'absolute', md: 'static' },
            top: { xs: 8, md: 'auto' },
            right: { xs: 8, md: 'auto' },
            zIndex: 1
          }}
        >
          <i
            className="fa-solid fa-pen-to-square"
            onClick={() => {
              setEditProfile(profile);
              setEditOpen(true);
            }}
            style={{ fontSize: 18, color: '#888', cursor: 'pointer' }}
          ></i>
        </Box>
      </Box>

      {/* Edit Profile Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} PaperProps={{ sx: { minWidth: 340, maxWidth: 420 } }}>
        <DialogTitle>Edit Profile</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
            <Box sx={{ position: 'relative', width: 80, height: 80, mb: 2 }}>
              <Avatar
                src={editProfile.image}
                sx={{ width: '100%', height: '100%', opacity: isImageLoading ? 0.5 : 1 }}
              />
              {isImageLoading && (
                <CircularProgress 
                  size={40} 
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: '-20px',
                    marginLeft: '-20px',
                    color: '#CB4422'
                  }}
                />
              )}
            </Box>
            <Button
              variant="outlined"
              component="label"
              sx={{ mb: 1, borderRadius: 10, fontSize: 13, textTransform: 'none', px: 3, borderColor: '#bbb', color: '#222', fontWeight: 400, bgcolor: '#fff' }}
            >
              Change Photo
              <input
                type="file"
                accept="image/*"
                hidden
                ref={fileInputRef}
                onChange={handleImageChange}
              />
            </Button>
          </Box>
          <TextField
            margin="dense"
            label="Name"
            fullWidth
            value={editProfile.name}
            onChange={e => setEditProfile(prev => ({ ...prev, name: e.target.value }))}
          />
          <TextField
            margin="dense"
            label="Phone"
            fullWidth
            value={editProfile.phone}
            onChange={e => setEditProfile(prev => ({ ...prev, phone: e.target.value }))}
            error={!!errors.phone}
            helperText={errors.phone}
          />
          <TextField
            margin="dense"
            label="Email"
            fullWidth
            value={editProfile.email}
            onChange={e => setEditProfile(prev => ({ ...prev, email: e.target.value }))}
            error={!!errors.email || !!errors.emailExists}
            helperText={errors.email || errors.emailExists}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => !isSaving && setEditOpen(false)} 
            color="inherit" 
            disabled={isSaving}
            sx={{ 
              borderRadius: 10, 
              fontSize: 13, 
              textTransform: 'none', 
              px: 3, 
              border: '1px solid #ddd', 
              mr: 1,
              '&.Mui-disabled': {
                borderColor: '#f0f0f0',
                color: '#aaa'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveProfile} 
            color="primary" 
            variant="contained" 
            disabled={isSaving}
            sx={{ 
              borderRadius: 10, 
              fontSize: 13, 
              textTransform: 'none', 
              px: 3,
              minWidth: 120,
              '&.Mui-disabled': {
                bgcolor: '#f0f0f0',
                color: '#aaa'
              }
            }}
          >
            {isSaving ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogActions>

      </Dialog>
      {isSmallScreen && <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Button variant='outlined' size='small' sx={{
          placeSelf: 'center', alignSelf: 'center',
          mb: 2, px: 3, py: 1, fontSize: 13, textTransform: 'none', color: '#222', fontWeight: 500, width: 150,
          borderRadius: 1
        }} onClick={() => setLogoutOpen(true)} >
          Logout
        </Button>
      </Box>}

      {isSmallScreen && <Dialog open={logoutOpen} PaperProps={{
        sx: {
          minWidth: { xs: 300, md: 350 },   // Increase width
          maxWidth: 500,   // Optional: set a max width
          minHeight: 180,  // Increase height
          p: 2             // Optional: add padding
        }
      }} onClose={() => setLogoutOpen(false)}>
        <DialogTitle variant='h4' sx={{ fontWeight: 600 }}>Confirm Logout</DialogTitle>
        <DialogContent>
          <Typography variant='h3'>Are you sure you want to logout?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleLogout}
            color="error"

            variant="contained"
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>}
      {/* Recent Orders */}
      <Typography sx={{ fontWeight: 600, fontSize: 18, mb: 0.5, fontFamily: 'Poppins' }}>
        Recent Orders
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
          flexWrap: { xs: 'nowrap', md: 'wrap' },
          minHeight: 120,
          alignItems: 'center',
          justifyContent: orders?.length ? 'flex-start' : 'center',
          bgcolor: orders?.length ? 'transparent' : '#fafafa',
          borderRadius: 2,
          p: orders?.length ? 0 : 3,
          textAlign: 'center'
        }}
      >
        {orders?.length ? orders.slice(0, 2).map((order, idx) => (
          <React.Fragment key={idx}>
          <Box
            key={idx}
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: '#fafafa',
              borderRadius: 3,
              py: 2,
              gap: 2,
              minWidth: 270,
              maxWidth: 380,
              flex: 1,
              mb: { xs: 2, md: 0 }
            }}
          >
            {/* Book/Combo Image */}
            <Box
              sx={{
                width: 85,
                height: 85,
                bgcolor: "#e0e0e0",
                borderRadius: 2,
                flexShrink: 0,
                // <-- reduced margin-right
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {order.booking_items?.[0]?.book?.bookImages?.[0]?.imageUrl ? (
                <img
                  src={order.booking_items[0].book.bookImages[0].imageUrl}
                  alt={order.booking_items[0].book?.title || "Book"}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }}
                />
              ) : order.booking_items?.[0]?.comboOffer?.imageUrl ? (
                <img
                  src={order.booking_items[0].comboOffer.imageUrl}
                  alt={order.booking_items[0].comboOffer?.title || "Combo"}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }}
                />
              ) : null}
            </Box>
            {/* Book/Combo Info and Actions */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'start',
                justifyContent: 'center',
                height: '100%'
              }}
            >
              <Typography fontWeight={600} sx={{ fontSize: 15, mb: 1, fontFamily: 'Poppins', textAlign: 'center' }}>
                {order.booking_items?.[0]?.book?.title ||
                  order.booking_items?.[0]?.comboOffer?.title ||
                  'No items in this order'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    borderRadius: 10,
                    fontSize: 10,
                    textTransform: 'none',
                    px: 2,
                    borderColor: '#bbb',
                    color: '#222',
                    fontWeight: 400,
                    bgcolor: '#fff'
                  }}
                  onClick={() => handleBuyNow(order.booking_items?.[0])}
                >
                  Buy it again
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    borderRadius: 10,
                    fontSize: 10,
                    textTransform: 'none',
                    px: 2,
                    borderColor: '#bbb',
                    color: '#222',
                    fontWeight: 400,
                    bgcolor: '#fff'
                  }}
                  onClick={() => {
                    if (order.booking_items?.[0]?.book?.id) {
                      setTimeout(() => navigate(`/book-overview/${order.booking_items[0].book.id}`), 0);
                    } else if (order.booking_items?.[0]?.comboOffer?.id) {
                      setTimeout(() => navigate(`/combo-overview/${order.booking_items[0].comboOffer.id}`), 0);
                    }
                  }}

                >
                  View your item
                </Button>
              </Box>
            </Box>
          </Box>
          </React.Fragment>
        )) : (
          <Box sx={{ textAlign: 'center', p: 3 }}>
            <i className="fas fa-shopping-bag" style={{ fontSize: 32, color: '#ccc', marginBottom: 16 }}></i>
            <Typography variant="body1" sx={{ color: '#666', mb: 1, fontSize: 15 }}>
              No recent orders found
            </Typography>
            <Button 
              variant="contained" 
              size="small"
              onClick={() => navigate('/')}
              sx={{
                bgcolor: '#CB4422',
                color: 'white',
                textTransform: 'none',
                borderRadius: 2,
                px: 3,
                py: 1,
                '&:hover': {
                  bgcolor: '#a8371b'
                }
              }}
            >
              Continue Shopping
            </Button>
          </Box>
        )}
      </Box>
    </>
  );
};

export default MyProfileInfo;